import { useCallback, useEffect, useRef } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useAtomValue } from 'jotai';
import { getAttachmentKey, type Attachment } from '@/types/approval';
import { buildCancelSubject } from '@/utils/approval/cancelApproval';
import { corporateSealKey } from '@/utils/approval/corporateSeal';
import { entExpenseKey } from '@/utils/approval/entExpense';
import { newCorporateCardKey } from '@/utils/approval/newCorporateCard';
import { sapContractFeeKey } from '@/utils/approval/sapContractFee';
import { mortgageFeeKey } from '@/utils/approval/mortgageFee';
import { storeOpenKey } from '@/utils/approval/storeOpen';
import { corporateCardRequestKey } from '@/utils/approval/corporateCardRequest';
import { annualLeaveRequestKey } from '@/utils/approval/annualLeaveRequest';
import { longServiceLeaveKey } from '@/utils/approval/longServiceLeave';
import { probationEvaluationKey } from '@/utils/approval/probationEvaluation';
import { salesBrandPermissionKey } from '@/utils/approval/salesBrandPermission';
import { groupSalesKey } from '@/utils/approval/groupSales';
import { marketingCaseKey } from '@/utils/approval/marketingCase';
import { productionVendorKey } from '@/utils/approval/productionVendor';
import { productLifecycleKey } from '@/utils/approval/productLifecycle';
import { styleMasterPriceKey } from '@/utils/approval/styleMasterPrice';
import { productionCostClosingKey } from '@/utils/approval/productionCostClosing';
import { claimClosingKey } from '@/utils/approval/claimClosing';
import { inventoryReplenishmentKey } from '@/utils/approval/inventoryReplenishment';
import { storageMaterialOrderKey } from '@/utils/approval/storageMaterialOrder';
import { supplierEvaluationKey } from '@/utils/approval/supplierEvaluation';
import { purchaseRequisitionKey } from '@/utils/approval/purchaseRequisition';
import { costQuotationKey } from '@/utils/approval/costQuotation';
import { salesContractKey } from '@/utils/approval/salesContract';
import { creditNoteKey } from '@/utils/approval/creditNote';
import { claimNoteKey } from '@/utils/approval/claimNote';
import { groupSalesMultiKey } from '@/utils/approval/groupSalesMulti';
import { sapBudgetIncreaseKey } from '@/utils/approval/sapBudgetIncrease';
import { sapBondStatusKey } from '@/utils/approval/sapBondStatus';
import { workSpecChecklistKey } from '@/utils/approval/workSpecification';
import { discountVoucherKey } from '@/utils/approval/discountVoucher';
import { expenseVoucherKey } from '@/utils/approval/expenseVoucher';
import { influencerPplKey } from '@/utils/approval/influencerPpl';
import { productionCostKey } from '@/utils/approval/productionCost';
import { externalLoanKey } from '@/utils/approval/externalLoan';
import {
  approvalCreateApprovalLineAtom,
  approvalCreateReferrersAtom,
} from './atoms';
import type { ApprovalFormValues } from './types';

const WARN_MESSAGE =
  '작성화면을 닫으시겠어요?\n임시저장 되지 않은 내용은 사라집니다.';

type Baseline = {
  subject: string;
  attachments: string;
  secuLevel: string;
  restrictReferrer: boolean;
  showImages: boolean;
  approvalLine: string;
  referrers: string;
};

const attachmentsKey = (list: Attachment[] | undefined): string =>
  (list ?? []).map(getAttachmentKey).join('|');

type Params = {
  isEditMode: boolean;
  // 편집 모드 하이드레이션(서버 저장값 setValue + 참조인 atom) 완료 신호. 이 시점 이후 baseline 을 캡처한다.
  contentReady: boolean;
  methods: UseFormReturn<ApprovalFormValues>;
  /** 양식 기본 제목 — 자동 채움값이라 '작성함' 판정에서 제외 */
  formDefaultSubject?: string | null;
  /** 양식 고정 결재자 id — 작성자가 지울 수 없는 자동 추가분이라 판정에서 제외 */
  formFixedApproverIds?: ReadonlySet<string>;
  /** 양식 고정 협조결재자 id — 작성자가 지울 수 없는 자동 추가분이라 판정에서 제외 */
  formFixedCoApproverIds?: ReadonlySet<string>;
  /** 양식 고정 참조인 id — 위와 같음 */
  formFixedReferrerIds?: ReadonlySet<string>;
};

/**
 * 작성/편집 이탈 가드. 브라우저 창 닫기·새로고침(beforeunload)에 경고를 걸고,
 * 폼 나가기(취소 버튼) 확인에 쓸 판정 함수(shouldWarnOnExit)를 돌려준다.
 * 모달 X 닫기는 편집 호스트(ApprovalModal)가 exitGuardRef 로 이 판정을 받아 담당.
 *
 * 판정 기준:
 * - 작성(create): "작성한 내용이 있으면" 경고 — 제목/첨부/결재선/참조인 중 하나라도 값 존재하거나
 *   사용자가 본문을 편집. (본문은 편집 모드와 같은 이유로 값 비교 불가 — 아래 참고.)
 * - 편집(edit): "불러온 뒤 바뀌었으면"만 경고 — 로드 완료 시점 baseline 대비 변경분.
 *   본문(Lexical)은 로드 시 자체 정규화 onChange 로 값 비교가 항상 "변경"처럼 보여 값 비교 불가 →
 *   contenteditable 편집 이벤트(입력·붙여넣기·잘라내기·드롭)로 "사용자가 본문을 편집했는지"와
 *   이미지/표 추가 여부만 감지. 그 외 필드는 baseline 값 비교.
 * - 저장 성공 후: captureSaveBaseline(전송 시 캡처→성공 시 커밋)이 저장 시점을 새
 *   baseline 으로 — 이후 변경분만 경고. (create 도 첫 저장 후엔 baseline diff 로 판정.)
 */
export default function useExitGuard({
  isEditMode,
  contentReady,
  methods,
  formDefaultSubject,
  formFixedApproverIds,
  formFixedCoApproverIds,
  formFixedReferrerIds,
}: Params): {
  shouldWarnOnExit: () => boolean;
  captureSaveBaseline: () => () => void;
} {
  const approvalLine = useAtomValue(approvalCreateApprovalLineAtom);
  const referrers = useAtomValue(approvalCreateReferrersAtom);

  // atom 은 렌더 시점 값이라, beforeunload/취소가 fire 되는 시점의 최신값을 읽도록 ref 로 미러링.
  const approvalLineRef = useRef(approvalLine);
  approvalLineRef.current = approvalLine;
  const referrersRef = useRef(referrers);
  referrersRef.current = referrers;
  // 양식 메타는 비동기 도착 — deps 로 받으면 첫 렌더의 null 이 판정에 고정된다.
  const formDefaultSubjectRef = useRef(formDefaultSubject);
  // 자동채움 이후 언어가 바뀌면 기본 제목도 바뀜 — 채운 시점 값으로 고정
  if (!contentReady) formDefaultSubjectRef.current = formDefaultSubject;
  const formFixedApproverIdsRef = useRef(formFixedApproverIds);
  formFixedApproverIdsRef.current = formFixedApproverIds;
  const formFixedCoApproverIdsRef = useRef(formFixedCoApproverIds);
  formFixedCoApproverIdsRef.current = formFixedCoApproverIds;
  const formFixedReferrerIdsRef = useRef(formFixedReferrerIds);
  formFixedReferrerIdsRef.current = formFixedReferrerIds;

  // 본문 편집은 값 비교가 안 돼(아래 참고) 이벤트 발생 횟수로 추적한다. 저장 커밋 시점의
  // 횟수(savedContentSeqRef)와 비교해 "그 이후 편집이 있었는지"만 본다 — boolean 이면
  // 저장 요청 in-flight 중의 편집까지 커밋 때 같이 지워진다.
  const contentEditSeqRef = useRef(0);
  const savedContentSeqRef = useRef(0);
  const baselineRef = useRef<Baseline | null>(null);
  const baselineDoneRef = useRef(false);

  // 본문 편집 감지: 문서 내 contenteditable(=BoardEditor 본문)에서 사용자 편집 이벤트가
  // 발생하면 편집으로 간주. 프로그램적 하이드레이션(setValue)은 이 이벤트들을 발생시키지
  // 않아 로드와 사용자 편집이 구분된다.
  //
  // input 만으로는 부족하다 — Lexical 은 붙여넣기/잘라내기/드롭을 preventDefault 후
  // editor.update() 로 직접 반영하므로 브라우저의 input 이벤트가 발생하지 않는다.
  // (붙여넣기만 한 본문이 경고 없이 사라지던 원인) 해당 이벤트들을 함께 본다.
  useEffect(() => {
    const onUserEdit = (event: Event) => {
      const target = event.target;
      if (
        target instanceof HTMLElement &&
        target.closest('[contenteditable="true"]')
      ) {
        contentEditSeqRef.current += 1;
      }
    };
    const events = ['input', 'paste', 'cut', 'drop'] as const;
    events.forEach((type) => document.addEventListener(type, onUserEdit, true));
    return () =>
      events.forEach((type) =>
        document.removeEventListener(type, onUserEdit, true),
      );
  }, []);

  // 툴바로 이미지/표를 삽입한 경우는 contenteditable 에 편집 이벤트가 오지 않는다.
  // 하이드레이션 시점의 개수를 기준으로 늘어났는지만 본다 — 에디터 정규화는
  // 이미지/표를 새로 만들어내지 않으므로 오탐이 없다.
  const countMedia = (html: string | undefined) =>
    (html?.match(/<(img|table)\b/gi) ?? []).length;
  const baselineMediaRef = useRef<number | null>(null);

  // 연결 계약(117·118)은 법틀 진입 시 자동 등록분이 있어 "값 존재" 가 아니라 기준선 대비
  // 변경으로 판정 — 미디어 기준선과 같은 시점에 두 모드 공통으로 캡처
  const contractIdsKey = (ids: number[] | undefined) => (ids ?? []).join(',');
  const baselineContractIdsRef = useRef<string | null>(null);

  // 사전 비용(205) 견적 — 편집 복원·초기 빈 블록이 있어 기준선 대비 변경으로 판정.
  // 행 id 는 복원마다 새로 붙으므로 비교에서 제외
  const expenseKey = (v: ApprovalFormValues) =>
    (v.expensePreApproval
      ? JSON.stringify([
          v.expensePreApproval.costType,
          v.expensePreApproval.vendors.map(({ id: _id, details, ...rest }) => [
            rest,
            details.map(({ id: _rowId, ...row }) => row),
          ]),
        ])
      : '') +
    entExpenseKey(v.entExpense) +
    workSpecChecklistKey(v.workSpecChecklist) +
    newCorporateCardKey(v.newCorporateCard) +
    (v.bpRegistration ? JSON.stringify(v.bpRegistration) : '') +
    corporateSealKey(v.corporateSeal) +
    sapContractFeeKey(v.sapContractFee) +
    mortgageFeeKey(v.mortgageFee) +
    storeOpenKey(v.storeOpen) +
    corporateCardRequestKey(v.corporateCardRequest) +
    annualLeaveRequestKey(v.annualLeaveRequest) +
    longServiceLeaveKey(v.longServiceLeave) +
    probationEvaluationKey(v.probationEvaluation) +
    salesBrandPermissionKey(v.salesBrandPermission) +
    groupSalesKey(v.groupSales) +
    marketingCaseKey(v.marketingCase) +
    sapBondStatusKey(v.sapBondStatus) +
    sapBudgetIncreaseKey(v.sapBudgetIncrease) +
    discountVoucherKey(v.discountVoucher) +
    expenseVoucherKey(v.expenseVoucher) +
    productionVendorKey(v.productionVendor) +
    productLifecycleKey(v.productLifecycle) +
    styleMasterPriceKey(v.styleMasterPrice) +
    productionCostClosingKey(v.productionCostClosing) +
    claimClosingKey(v.claimClosing) +
    inventoryReplenishmentKey(v.inventoryReplenishment) +
    storageMaterialOrderKey(v.storageMaterialOrder) +
    supplierEvaluationKey(v.supplierEvaluation) +
    purchaseRequisitionKey(v.purchaseRequisition) +
    costQuotationKey(v.costQuotation) +
    salesContractKey(v.salesContract) +
    creditNoteKey(v.creditNote) +
    claimNoteKey(v.claimNote) +
    groupSalesMultiKey(v.groupSalesMulti) +
    influencerPplKey(v.influencerPpl) +
    productionCostKey(v.productionCost) +
    externalLoanKey(v.externalLoan);
  const baselineExpenseRef = useRef<string | null>(null);

  // 매장 OPEN(233) — 분석 결과는 파이프라인 단위로 통째 바뀌므로 id 로 판정
  const openCostKey = (v: ApprovalFormValues) =>
    v.openConstructionCost?.aiPipelineId ?? '';
  const baselineOpenCostRef = useRef<string | null>(null);

  const snapshot = useCallback((): Baseline => {
    const v = methods.getValues();
    return {
      subject: v.subject ?? '',
      attachments: attachmentsKey(v.attachments),
      secuLevel: v.secuLevel ?? '',
      restrictReferrer: !!v.restrictReferrer,
      showImages: !!v.showImagesBelowContent,
      approvalLine: JSON.stringify(approvalLineRef.current),
      referrers: JSON.stringify(referrersRef.current),
    };
  }, [methods]);

  // 편집 모드 baseline 1회 캡처. contentReady(상위 필드 하이드레이션 완료) 이후 rAF 로
  // 결재선/참조인 atom 하이드레이션까지 정착시킨 뒤 스냅샷.
  // 본문 이미지/표 기준선은 두 모드 공통으로 같은 시점에 캡처한다.
  useEffect(() => {
    if (!contentReady) return;
    if (baselineDoneRef.current) return;
    const raf = requestAnimationFrame(() => {
      baselineDoneRef.current = true;
      baselineMediaRef.current = countMedia(methods.getValues().content);
      baselineContractIdsRef.current = contractIdsKey(
        methods.getValues().contractIds,
      );
      baselineExpenseRef.current = expenseKey(methods.getValues());
      baselineOpenCostRef.current = openCostKey(methods.getValues());
      if (isEditMode) baselineRef.current = snapshot();
    });
    return () => cancelAnimationFrame(raf);
  }, [isEditMode, contentReady, snapshot, methods]);

  const hasAddedMedia = useCallback((): boolean => {
    const base = baselineMediaRef.current;
    if (base === null) return false; // 기준선 캡처 전(로드 중)에는 판정하지 않음
    return countMedia(methods.getValues().content) > base;
  }, [methods]);

  const hasChangedContractIds = useCallback((): boolean => {
    const base = baselineContractIdsRef.current;
    if (base === null) return false;
    return contractIdsKey(methods.getValues().contractIds) !== base;
  }, [methods]);

  const hasChangedExpense = useCallback((): boolean => {
    const base = baselineExpenseRef.current;
    if (base === null) return false;
    return expenseKey(methods.getValues()) !== base;
  }, [methods]);

  const hasChangedOpenCost = useCallback((): boolean => {
    const base = baselineOpenCostRef.current;
    if (base === null) return false;
    return openCostKey(methods.getValues()) !== base;
  }, [methods]);

  const hasCreateContent = useCallback((): boolean => {
    const v = methods.getValues();
    // 제목은 취소 품의(원 품의명)·SA 출장비(177·179) SAP 자동 제목·SERP 재고실사 조정(169)·매장 변경(162)·개점(161)·여신유예신청(163)·폐점 정산(181)·중간관리자 교체 정산(182)·재고실사 조정 다중(210)·시즌마감 조정(174) 자동 제목·양식 기본 제목으로 자동 채워짐 — 값 존재가 아니라 첫 로드값
    // 대비 변경으로 판정해야 자동값을 지워 비운 것도 변경으로 잡힌다.
    const initialSubject =
      (v.cancelTarget ? buildCancelSubject(v.cancelTarget.subject) : null) ??
      v.saTravelExpense?.autoSubject ??
      v.serpStockAdjustment?.autoSubject ??
      v.serpStoreChange?.autoSubject ??
      v.serpStoreOpen?.autoSubject ??
      v.sapCollateralChange?.autoSubject ??
      v.accountingBp?.autoSubject ??
      v.influencerMonthlyReport?.autoSubject ??
      v.serpLendingDelay?.autoSubject ??
      v.serpStoreClosure?.autoSubject ??
      v.serpIntermediaryReplace?.autoSubject ??
      v.serpBulkStockAdjustment?.autoSubject ??
      v.serpSeasonEnd?.autoSubject ??
      v.influencerPpl?.autoSubject ??
      formDefaultSubjectRef.current ??
      '';
    if ((v.subject ?? '') !== initialSubject) return true;
    if (hasChangedContractIds()) return true;
    if (hasChangedExpense()) return true;
    if (hasChangedOpenCost()) return true;
    // 본문은 값 전체로는 판정 불가 — 양식 contentTemplate 자동채움과 에디터 mount 시 자체
    // 정규화(빈 문단 HTML)만으로도 항상 값이 차 있어 "작성함"처럼 보인다.
    // 편집 모드와 동일하게 사용자가 실제로 본문을 편집했는지(+이미지/표 추가)로 판정.
    if (contentEditSeqRef.current > savedContentSeqRef.current) return true;
    if (hasAddedMedia()) return true;
    if (v.attachments && v.attachments.length > 0) return true;
    // 결재선·참조인의 양식 고정분은 자동 추가라 작성한 내용이 아니다 — 잠겨 있어 지울 수도 없다.
    const line = approvalLineRef.current;
    const fixedApproverIds = formFixedApproverIdsRef.current;
    const addedApprovers = line.approvers.filter(
      (m) => !fixedApproverIds?.has(m.passportId),
    );
    const fixedCoApproverIds = formFixedCoApproverIdsRef.current;
    const addedCoApprovers = (line.cooperation?.members ?? []).filter(
      (m) => !fixedCoApproverIds?.has(m.passportId),
    );
    if (addedApprovers.length > 0 || addedCoApprovers.length > 0) return true;
    const fixedReferrerIds = formFixedReferrerIdsRef.current;
    if (referrersRef.current.some((r) => !fixedReferrerIds?.has(r.id))) {
      return true;
    }
    return false;
  }, [
    methods,
    hasAddedMedia,
    hasChangedContractIds,
    hasChangedExpense,
    hasChangedOpenCost,
  ]);

  const hasEditChanges = useCallback((): boolean => {
    const base = baselineRef.current;
    if (!base) return false; // baseline 캡처 전(로드 중)에는 변경 없음으로 간주
    if (contentEditSeqRef.current > savedContentSeqRef.current) return true;
    if (hasAddedMedia()) return true;
    if (hasChangedContractIds()) return true;
    if (hasChangedExpense()) return true;
    if (hasChangedOpenCost()) return true;
    const cur = snapshot();
    // SAP 담보 신규(154)·변경(155)·회계거래처(101)·인플루언서 월마감(108)·PPL 정산(135)·SERP 시즌마감 조정(174)은 편집 진입 뒤 조회가 끝나면 제목을 덮어씀 — 그 값까지는 변경 아님
    const formValues = methods.getValues();
    const autoSubject =
      formValues.sapCollateralChange?.autoSubject ??
      formValues.accountingBp?.autoSubject ??
      formValues.influencerMonthlyReport?.autoSubject ??
      formValues.influencerPpl?.autoSubject ??
      formValues.serpSeasonEnd?.autoSubject;
    return (
      (cur.subject !== base.subject && cur.subject !== autoSubject) ||
      cur.attachments !== base.attachments ||
      cur.secuLevel !== base.secuLevel ||
      cur.restrictReferrer !== base.restrictReferrer ||
      cur.showImages !== base.showImages ||
      cur.approvalLine !== base.approvalLine ||
      cur.referrers !== base.referrers
    );
  }, [
    methods,
    snapshot,
    hasAddedMedia,
    hasChangedContractIds,
    hasChangedExpense,
    hasChangedOpenCost,
  ]);

  // fire 시점 최신 판정을 읽도록 ref 고정(리스너 재등록 없이).
  const shouldWarnRef = useRef<() => boolean>(() => false);
  shouldWarnRef.current = () =>
    isEditMode || baselineRef.current ? hasEditChanges() : hasCreateContent();

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!shouldWarnRef.current()) return;
      event.preventDefault();
      event.returnValue = WARN_MESSAGE;
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const shouldWarnOnExit = useCallback(() => shouldWarnRef.current(), []);

  // 저장 요청 전송 시점에 호출해 그때의 폼 상태를 캡처하고, 반환된 커밋 함수를 저장
  // 성공 시 호출해 baseline 으로 확정한다. 응답 시점 값을 쓰면 요청 in-flight 중의
  // 편집분이 "저장됨"으로 흡수돼 경고 없이 유실된다. 실패 시 커밋하지 않으면 원상태.
  const captureSaveBaseline = useCallback(() => {
    const media = countMedia(methods.getValues().content);
    const contracts = contractIdsKey(methods.getValues().contractIds);
    const expense = expenseKey(methods.getValues());
    const openCost = openCostKey(methods.getValues());
    const saved = snapshot();
    const seq = contentEditSeqRef.current;
    return () => {
      baselineMediaRef.current = media;
      baselineContractIdsRef.current = contracts;
      baselineExpenseRef.current = expense;
      baselineOpenCostRef.current = openCost;
      baselineRef.current = saved;
      savedContentSeqRef.current = seq;
    };
  }, [methods, snapshot]);

  return { shouldWarnOnExit, captureSaveBaseline };
}
